// # Step 2 Library Component
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../auth/state";
import "./library_step_2.css";
import "../lib_components/lib_animation.css"; // # Import animations
import DashboardFooter from "../../../components/DashboardFooter";
import LibraryHeader from "../lib_components/lib_header";
import WarningFlood from "../lib_components/warning_flood"; // # Import WarningFlood component
import { doc, updateDoc, onSnapshot } from "firebase/firestore"; // # Import for updating theme
import { db } from "../../../auth/firebase"; // # Import db
import cacheManager from "../../../auth/cache_manager"; // # Import cache manager
import { FaChartLine, FaCheckCircle } from "react-icons/fa"; // # Import stats icon and checkmark icon

// # Import icons
import anatomyIcon from "../step1/step1_icons/anatomy.png";
import biochemistryIcon from "../step1/step1_icons/biochemistry.png";
import bloodIcon from "../step1/step1_icons/blood.png";
import cnsIcon from "../step1/step1_icons/cns.png";
import earIcon from "../step1/step1_icons/ear.png";
import endocrineIcon from "../step1/step1_icons/endocrine.png";
import emergencyIcon from "../step1/step1_icons/emergency.png";
import eyeIcon from "../step1/step1_icons/eye.png";
import gitIcon from "../step1/step1_icons/git.png";
import healthcareIcon from "../step1/step1_icons/healthcare.png";
import heartIcon from "../step1/step1_icons/heart.png";
import immunologyIcon from "../step1/step1_icons/immunology.png";
import infectiousIcon from "../step1/step1_icons/infectious.png";
import kidneyIcon from "../step1/step1_icons/kidney.png";
import lungsIcon from "../step1/step1_icons/lungs.png";
import musculoskeletalIcon from "../step1/step1_icons/musculoskeletal.png";
import newbornIcon from "../step1/step1_icons/newborn.png";
import pharmaIcon from "../step1/step1_icons/pharma.png";
import psychiatryIcon from "../step1/step1_icons/psychiatry.png";
import publicHealthIcon from "../step1/step1_icons/public_health.png";
import qualityIcon from "../step1/step1_icons/quality.png";
import reproductiveIcon from "../step1/step1_icons/reproductive-system.png";
import pregnantIcon from "../step1/step1_icons/pregnant.png";
import skinIcon from "../step1/step1_icons/skin.png";
import statisticsIcon from "../step1/step1_icons/statistics.png";
import surgeryIcon from "../step1/step1_icons/surgery.png";
import wellPatientIcon from "../step1/step1_icons/well_patient.png";

// # Import chapter topics for dynamic counting
import { chapterTopics } from "./step2_chapter.js";

// # Chapter data with icons
export const chapters = [
  { id: 1, title: "CARDIOVASCULAR", icon: heartIcon, enabled: true },
  { id: 2, title: "RESPIRATORY_&_ENT", icon: lungsIcon, enabled: true },
  { id: 3, title: "RENAL_&_UROLOGY", icon: kidneyIcon, enabled: true },
  { id: 4, title: "ENDOCRINE", icon: endocrineIcon, enabled: true },
  { id: 5, title: "HEMATOLOGY_&_ONCOLOGY", icon: bloodIcon, enabled: true },
  { id: 6, title: "IMMUNOLOGY", icon: immunologyIcon, enabled: true },
  { id: 7, title: "GASTROINTESTINAL", icon: gitIcon, enabled: true },
  { id: 8, title: "MUSCULOSKELETAL", icon: musculoskeletalIcon, enabled: true },
  { id: 9, title: "NEUROLOGY", icon: cnsIcon, enabled: true },
  { id: 10, title: "OPHTHALMOLOGY", icon: eyeIcon, enabled: true },
  { id: 11, title: "PSYCHIATRY", icon: psychiatryIcon, enabled: true },
  { id: 12, title: "DERMATOLOGY", icon: skinIcon, enabled: true },
  { id: 13, title: "GYNECOLOGY", icon: reproductiveIcon, enabled: true },
  { id: 14, title: "OBSTETRICS", icon: pregnantIcon, enabled: true },
  { id: 15, title: "NEONATE_&_INFANT", icon: newbornIcon, enabled: true },
  { id: 16, title: "GENETICS", icon: biochemistryIcon, enabled: false },
  { id: 17, title: "INFECTIOUS_DISEASES", icon: infectiousIcon, enabled: true },
  { id: 18, title: "SURGERY_&_ANESTHESIA", icon: surgeryIcon, enabled: false },
  {
    id: 19,
    title: "EMERGENCY_AND_TOXICOLOGY",
    icon: emergencyIcon,
    enabled: true,
  },
  { id: 20, title: "THE_WELL_PATIENT", icon: wellPatientIcon, enabled: true },
  {
    id: 21,
    title: "BIOSTATISTICS_&_EPIDEMIOLOGY",
    icon: statisticsIcon,
    enabled: true,
  },
  {
    id: 22,
    title: "ETHICS_&_COMMUNICATION",
    icon: publicHealthIcon,
    enabled: true,
  },
  { id: 23, title: "HEALTHCARE_DELIVERY", icon: healthcareIcon, enabled: true },
  { id: 24, title: "QUALITY_&_SAFETY", icon: qualityIcon, enabled: true },
  { id: 25, title: "PHARMACOLOGY_REVIEW", icon: pharmaIcon, enabled: false },
];

// # Calculate total enabled chapters dynamically from the source of truth
const TOTAL_ENABLED_CHAPTERS = chapters.filter(
  (chapter) => chapter.enabled,
).length;

// # Calculate total topics from enabled chapters
const TOTAL_HARDCODED_TOPICS = chapters.reduce((total, chapter) => {
  if (chapter.enabled) {
    // # Transform chapter title to match chapterTopics keys exactly
    let chapterSlug = chapter.title
      .toLowerCase()
      .replace(/,/g, "") // Remove commas
      .replace(/_&_/g, "_and_") // Replace _&_ with _and_
      .replace(/&/g, "and") // Replace remaining & with and
      .replace(/\s+/g, "_") // Replace spaces with underscore
      .replace(/_+/g, "_") // Replace multiple underscores with single one
      .replace(/^_|_$/g, ""); // Remove leading/trailing underscores

    // # Special case mappings
    const slugMappings = {
      respiratory_and_ent: "respiratory_and_ent",
      renal_and_urology: "renal_and_urology",
      hematology_and_oncology: "hematology_and_oncology",
      gastrointestinal: "gastrointestinal",
      gynecology_and_obstetrics: "gynecology_and_obstetrics",
      neonate_and_infant: "neonate_and_infant",
    };

    // # Use mapping if exists, otherwise use transformed slug
    const finalSlug = slugMappings[chapterSlug] || chapterSlug;

    // # Add to total if topics exist for this chapter
    return total + (chapterTopics[finalSlug]?.length || 0);
  }
  return total;
}, 0); // # Remove fallback to 100 since we want exact count

function LibraryStep2() {
  const { userProfile, user } = useAuthStore();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false); // # Add dark mode state
  const [libraryStats, setLibraryStats] = useState({
    completedTopics: 0,
    totalTopics: 0,
    completedChapters: 0,
    totalChapters: 0,
  });
  const [chapterData, setChapterData] = useState({}); // # Add state for chapter data

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (
          userData &&
          userData.themes &&
          userData.themes.lib_theme !== undefined
        ) {
          setIsDarkMode(userData.themes.lib_theme === "dark");
        }
      }
    };

    loadThemePreference();
  }, [user]);

  // # Load library progress from Firestore
  useEffect(() => {
    if (!user?.uid) return;

    const progressRef = doc(db, "my_lib_progress", user.uid);
    const unsubscribe = onSnapshot(progressRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.step2) {
          let completedTopics = 0;
          let totalTopics = 0;
          let completedChapters = 0;

          Object.keys(data.step2).forEach((chapter) => {
            const chapterData = data.step2[chapter];
            // # Get all topic keys (excluding special fields)
            const topicKeys = Object.keys(chapterData).filter(
              (key) =>
                key !== "iscomplete" &&
                key !== "iscomplete_at" &&
                key !== "IsChapStarted" &&
                key !== "ChptStartedAt",
            );

            // # Count completed topics
            const chapterCompletedTopics = topicKeys.filter(
              (topic) => chapterData[topic]?.completed,
            ).length;

            // # Get total topics for this chapter from chapterTopics mapping
            const chapterTotalTopics = chapterTopics[chapter]?.length || 0;

            completedTopics += chapterCompletedTopics;
            totalTopics += chapterTotalTopics;

            if (chapterData.iscomplete) {
              completedChapters++;
            }
          });

          setLibraryStats({
            completedTopics,
            totalTopics: TOTAL_HARDCODED_TOPICS,
            completedChapters,
            totalChapters: TOTAL_ENABLED_CHAPTERS,
          });

          // Store chapter completion data
          setChapterData(data.step2);
        }
      }
    });

    return () => unsubscribe();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);

      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, "users", user.uid), {
          "themes.lib_theme": newTheme ? "dark" : "light",
        });

        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              lib_theme: newTheme ? "dark" : "light",
            },
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }

      // # Dispatch theme change event for footer and other components
      const themeChangeEvent = new CustomEvent("theme_change", {
        detail: { isDark: newTheme },
      });
      window.dispatchEvent(themeChangeEvent);
    } catch (error) {
      console.error("Error updating theme preference:", error);
    }
  };

  // # Basic authentication check - no subscription check needed
  useEffect(() => {
    if (!user?.uid) {
      navigate("/signin");
    }
  }, [user, navigate]);

  // # Display title function
  const getDisplayTitle = (title) => {
    return title
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  // # Handle chapter click
  const handleChapterClick = (chapter) => {
    // # Transform chapter title to match chapterTopics keys exactly
    const chapterSlug = chapter.title
      .toLowerCase()
      .replace(/,/g, "") // Remove commas
      .replace(/_&_/g, "_and_") // Replace _&_ with _and_
      .replace(/&/g, "and") // Replace remaining & with and
      .replace(/\s+/g, "_") // Replace spaces with underscore
      .replace(/_+/g, "_") // Replace multiple underscores with single one
      .replace(/^_|_$/g, ""); // Remove leading/trailing underscores

    // # Special case mappings - same as above
    const slugMappings = {
      respiratory_and_ent: "respiratory_and_ent",
      renal_and_urology: "renal_and_urology",
      hematology_and_oncology: "hematology_and_oncology",
      gastrointestinal: "gastrointestinal",
      gynecology_and_obstetrics: "gynecology_and_obstetrics",
      neonate_and_infant: "neonate_and_infant",
    };

    // # Use mapping if exists, otherwise use transformed slug
    const finalSlug = slugMappings[chapterSlug] || chapterSlug;

    const isAdmin = window.location.pathname.startsWith("/admin");
    const basePath = isAdmin ? "/admin/library/step2" : "/library/step2";
    navigate(`${basePath}/${finalSlug}`);
  };

  // # Handle stats click
  const handleStatsClick = () => {
    const isAdmin = window.location.pathname.startsWith("/admin");
    const basePath = isAdmin ? "/admin/library/step2" : "/library/step2";
    navigate(`${basePath}/stats`);
  };

  return (
    <div
      className={`step2_library-container ${isDarkMode ? "step2_dark-mode" : ""}`}
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/icons/dash/usmle_step2.jpg)` }}
    >
      <WarningFlood />
      <LibraryHeader
        isDarkMode={isDarkMode}
        onThemeToggle={handleThemeToggle}
      />

      {/* Main Content */}
      <div className="step2_library-main-content">
        <div className="step2_page-title">
          <h1>
            USMLE Step 2 Library -{" "}
            {userProfile?.firstName || user?.email?.split("@")[0]}
          </h1>
        </div>

        {/* Stats Card */}
        <div className="step2_stats-card">
          <div className="step2_stats-content">
            <div className="step2_stat-item">
              <span className="step2_stat-label">Topics Completed</span>
              <span className="step2_stat-value">
                {libraryStats.completedTopics}/{libraryStats.totalTopics}
              </span>
              <div className="step2_progress-bar">
                <div
                  className="step2_progress-fill"
                  style={{
                    width: `${(libraryStats.completedTopics / libraryStats.totalTopics) * 100}%`,
                  }}
                />
              </div>
            </div>

            <div className="step2_stat-item">
              <span className="step2_stat-label">Chapters Completed</span>
              <span className="step2_stat-value">
                {libraryStats.completedChapters}/{libraryStats.totalChapters}
              </span>
              <div className="step2_progress-bar">
                <div
                  className="step2_progress-fill"
                  style={{
                    width: `${(libraryStats.completedChapters / libraryStats.totalChapters) * 100}%`,
                  }}
                />
              </div>
            </div>

            <button className="step2_stats-button" onClick={handleStatsClick}>
              <FaChartLine />
              <span>View Stats</span>
            </button>
          </div>
        </div>

        <div className="step2_chapters-grid">
          {chapters
            .filter((chapter) => chapter.enabled)
            .map((chapter) => {
              // # Transform chapter title to match chapterTopics keys exactly - same as above
              const chapterSlug = chapter.title
                .toLowerCase()
                .replace(/,/g, "") // Remove commas
                .replace(/_&_/g, "_and_") // Replace _&_ with _and_
                .replace(/&/g, "and") // Replace remaining & with and
                .replace(/\s+/g, "_") // Replace spaces with underscore
                .replace(/_+/g, "_") // Replace multiple underscores with single one
                .replace(/^_|_$/g, ""); // Remove leading/trailing underscores

              // # Special case mappings - same as above
              const slugMappings = {
                respiratory_and_ent: "respiratory_and_ent",
                renal_and_urology: "renal_and_urology",
                hematology_and_oncology: "hematology_and_oncology",
                gastrointestinal: "gastrointestinal",
                gynecology_and_obstetrics: "gynecology_and_obstetrics",
                neonate_and_infant: "neonate_and_infant",
              };

              // # Use mapping if exists, otherwise use transformed slug
              const finalSlug = slugMappings[chapterSlug] || chapterSlug;

              // # Calculate chapter progress using the correct slug
              const chapterProgress = (() => {
                if (!chapterData[finalSlug]) return 0;

                // # If chapter is marked as complete, return 100%
                if (chapterData[finalSlug].iscomplete) return 100;

                // # Get total possible topics from chapterTopics mapping
                const totalPossibleTopics =
                  chapterTopics[finalSlug]?.length || 0;
                if (totalPossibleTopics === 0) return 0;

                // # Count completed topics (excluding special fields)
                const topicKeys = Object.keys(chapterData[finalSlug]).filter(
                  (key) =>
                    key !== "iscomplete" &&
                    key !== "iscomplete_at" &&
                    key !== "IsChapStarted" &&
                    key !== "ChptStartedAt",
                );

                const completedTopics = topicKeys.filter(
                  (key) => chapterData[finalSlug][key]?.completed,
                ).length;

                return Math.round(
                  (completedTopics / totalPossibleTopics) * 100,
                );
              })();

              return (
                <div
                  key={chapter.id}
                  className="step2_chapter-tile"
                  onClick={() => handleChapterClick(chapter)}
                >
                  <div className="step2_chapter-icon">
                    {chapter.icon ? (
                      <img
                        src={chapter.icon}
                        alt={chapter.title}
                        className="step2_chapter-icon-image"
                      />
                    ) : (
                      <div className="step2_icon-placeholder">{chapter.id}</div>
                    )}
                  </div>
                  <h3 className="step2_chapter-title">
                    {getDisplayTitle(chapter.title)}
                  </h3>

                  {/* Progress bar */}
                  <div className="step2_chapter-progress">
                    <div className="step2_chapter-progress-bar">
                      <div
                        className="step2_chapter-progress-fill"
                        style={{ width: `${chapterProgress}%` }}
                      />
                    </div>
                    <span className="step2_chapter-progress-text">
                      {chapterProgress}%
                    </span>
                  </div>

                  {/* Add verification icon for completed chapters */}
                  {chapterData && chapterData[finalSlug]?.iscomplete && (
                    <div className="step2_chapter-completed">
                      <FaCheckCircle className="step2_chapter-completed-icon" />
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {/* Footer */}
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default LibraryStep2;
